import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Briefcase,
  Plus,
  Edit,
  Phone,
  Mail,
  Wrench,
  CheckCircle2,
  XCircle
} from "lucide-react";

export default function VendorManagement() {
  const { unitId } = useParams();
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    specialty: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await api.get("/vendors");
      setVendors(res.data);
    } catch (error) {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedVendor(null);
    setFormData({
      company_name: "",
      contact_name: "",
      email: "",
      phone: "",
      specialty: "",
      notes: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      company_name: vendor.company_name,
      contact_name: vendor.contact_name,
      email: vendor.email,
      phone: vendor.phone,
      specialty: vendor.specialty,
      notes: vendor.notes || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      if (selectedVendor) {
        await api.put(`/vendors/${selectedVendor.id}`, formData);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", formData);
        toast.success("Vendor created");
      }
      setDialogOpen(false);
      fetchVendors();
    } catch (error) {
      toast.error("Failed to save vendor");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (vendor) => {
    try {
      await api.put(`/vendors/${vendor.id}`, { active: !vendor.active });
      toast.success(vendor.active ? "Vendor deactivated" : "Vendor activated");
      fetchVendors();
    } catch (error) {
      toast.error("Failed to update vendor");
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={unitId} />
      
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Vendor Management</h1>
              <p className="text-gray-600">Manage external service providers</p>
            </div>
          </div>

          {user?.role === "SystemAdmin" && (
            <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          )}
        </div>

        <Card className="bg-white border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            ) : vendors.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Specialty</TableHead>
                    <TableHead>Status</TableHead>
                    {user?.role === "SystemAdmin" && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell className="font-medium">{vendor.company_name}</TableCell>
                      <TableCell>{vendor.contact_name}</TableCell>
                      <TableCell>
                        <a href={`mailto:${vendor.email}`} className="text-indigo-600 hover:underline flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {vendor.email}
                        </a>
                      </TableCell>
                      <TableCell>
                        <a href={`tel:${vendor.phone}`} className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {vendor.phone}
                        </a>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          <Wrench className="w-3 h-3" />
                          {vendor.specialty}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={vendor.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                          {vendor.active !== false ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      {user?.role === "SystemAdmin" && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(vendor)}
                              data-testid={`edit-vendor-${vendor.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActive(vendor)}
                              data-testid={`toggle-vendor-${vendor.id}`}
                            >
                              {vendor.active !== false ? (
                                <XCircle className="w-4 h-4 text-red-500" />
                              ) : (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vendors</h3>
                <p className="text-gray-500 mb-4">Get started by adding your first vendor</p>
                {user?.role === "SystemAdmin" && (
                  <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vendor
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vendor Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{selectedVendor ? "Edit Vendor" : "Add New Vendor"}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="ABC Repairs LLC"
                  data-testid="vendor-company-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="vendor-contact-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@vendor.com"
                    data-testid="vendor-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    data-testid="vendor-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Specialty</Label>
                <Input
                  value={formData.specialty}
                  onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
                  placeholder="HVAC, Plumbing, Electrical..."
                  data-testid="vendor-specialty-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional information..."
                  rows={3}
                  data-testid="vendor-notes-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-vendor-btn"
              >
                {submitting ? "Saving..." : selectedVendor ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
